import { useState, useCallback } from "react";
import { Plus } from "lucide-react";
import { Badge } from "@/components/atoms/Badge";
import { legalEntitiesService } from "@/services/legalEntitiesService";
import { formatRelativeTime } from "@/lib/format";
import { SimpleListPage } from "@/modules/_shared/SimpleListPage";
import { CreateEntityModal } from "./CreateEntityModal";
import type { Column } from "@/components/organisms/DataTable";
import type { LegalEntityFilter, LegalEntitySummary } from "@/types/entities";

const columns: Column<LegalEntitySummary>[] = [
  {
    id: "displayName",
    header: "Entity",
    sortable: true,
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium text-bridge-fg">
          {r.displayName ?? r.legalName ?? "(unnamed)"}
        </div>
        <div className="truncate text-xs text-bridge-muted">{r.companyNumber ?? "no company number"}</div>
      </div>
    ),
  },
  { id: "tradingName", header: "Trading As", sortable: true, cell: (r) => r.tradingName ?? "—" },
  { id: "entityType", header: "Type", cell: (r) => r.entityType ?? "—" },
  {
    id: "status",
    header: "Status",
    sortable: true,
    cell: (r) => (r.status ? <Badge tone="info">{r.status}</Badge> : <span className="text-bridge-subtle">—</span>),
  },
  {
    id: "atlasModifiedAt",
    header: "Updated",
    sortable: true,
    align: "right",
    cell: (r) => <span className="text-bridge-muted">{formatRelativeTime(r.atlasModifiedAt)}</span>,
  },
];

export function LegalEntitiesPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCreated = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  return (
    <>
      <SimpleListPage<LegalEntitySummary, LegalEntityFilter>
        key={refreshKey}
        title="Legal Entities"
        fetcher={legalEntitiesService.list}
        columns={columns}
        rowKey={(r) => r.id}
        searchPlaceholder="Search by name or company number..."
        emptyTitle="No legal entities match"
        showPageLoader
        toolbar={() => (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-bridge-accent hover:bg-bridge-accentSoft rounded-lg shadow-sm transition-all"
          >
            <Plus size={14} />
            Create Entity
          </button>
        )}
      />
      <CreateEntityModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
}